import g from '#core/global';
import load from '#core/load';
import util from '#core/util';
import fastdom from 'fastdom';


const max_row_container_$ = '[class*=":g-max-"], [class*=":g-auto-"]';
const row_items_attr = 'data-row-items';
const total_items_attr = 'data-total-items';
const skip_columns_attr = 'data-skip-columns';
const proximity_threshold = 50;

const items_per_row_prop = '--items-per-row';
const item_min_width_prop = '--item-min-width';

let has_grid = false;
let $max_row_containers = null;
let resize_observer = null;
let mutation_observer = null;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('grid')) return false;

		load.content(() => {
			init_containers();
		});
	},

};

export default module;
module.init();


/**
 * Assign elements
 * @return {boolean}
 */
function assign_elements() {
	$max_row_containers = document.querySelectorAll(max_row_container_$);
	return $max_row_containers.length > 0;
}


/**
 * Check if container uses auto grid (.:g-auto-*)
 * @param {HTMLElement} $container
 * @return {boolean}
 */
function is_auto_grid($container) {
	return $container.classList.toString().includes(':g-auto-');
}


/**
 * Extract manual skip columns from :g-auto-skip-* classes
 * @param {HTMLElement} $container
 * @return {Array<int>|null} Array of column counts to skip, or null if no overrides
 */
function get_manual_skip_columns($container) {
	const classes = $container.classList.toString();
	const skip_pattern = /:g-auto-skip-(\d+)/g;
	const matches = [...classes.matchAll(skip_pattern)];

	if (matches.length === 0) return null;

	return matches.map(m => parseInt(m[1])).sort((a, b) => a - b);
}


/**
 * Calculate which column counts would create disproportionately small orphan rows
 * Only flag column counts where orphans are visually unbalanced (too few items on last row)
 * and intervention would be beneficial (fewer than 2 complete rows)
 * @param {int} total_items - Total number of items in container
 * @param {int} max_columns - Maximum columns from --items-per-row
 * @return {Array<int>} Array of all column counts to skip
 */
function calculate_orphan_columns(total_items, max_columns) {
	const orphan_columns = [];

	// Check each possible column count from 3 to max
	// (2 columns with 1 orphan is fine, so start at 3)
	for (let cols = 3; cols <= max_columns; cols++) {
		const remainder = total_items % cols;
		const full_rows = Math.floor(total_items / cols);

		// Skip this column count if:
		// 1. Remainder is disproportionately small (< half the column count)
		// 2. There are fewer than 2 full rows (to avoid pointless interventions)
		//
		// Examples:
		// - 5 items, 4 cols: 4+1 is bad (1 < 2, full_rows == 1) ... skip
		// - 7 items, 5 cols: 5+2 is bad (2 < 2.5, full_rows == 1) ... skip
		// - 13 items, 4 cols: 4+4+4+1 (full_rows == 3) ... don't skip
		// - 3+3+2 is okay (full_rows == 2) ... don't skip
		if (
			remainder > 0
			&& remainder < cols / 2
			&& full_rows < 2
		) {
			orphan_columns.push(cols);
		}
	}

	// Return all problematic columns so the system can handle multiple scenarios
	return orphan_columns;
}


/**
 * Calculate items per row in a grid container
 * @deprecated
 * @param {HTMLElement} $container
 * @return {int}
 */
function calculate_items_per_row_alt($container) {
	const $items = Array.from($container.children);
	if ($items.length <= 1) return $items.length;

	const first_top = $items[0].offsetTop;

	let items_per_row = 1;
	for (let i = 1; i < $items.length; i++) {
		if ($items[i].offsetTop !== first_top) break;

		items_per_row++;
	}
	return items_per_row;
}


/**
 * Calculate items per row in a grid container
 *
 * Returns the actual number of columns the grid is using at current viewport
 * width, as if there were unlimited items
 * @param {HTMLElement} $container
 * @param {int} threshold
 * @return {int}
 */
function calculate_items_per_row($container, threshold = proximity_threshold) {
	const $items = Array.from($container.children);
	if ($items.length === 0) return 0;

	// Single item case - calculate potential columns from container width
	if ($items.length === 1) {
		return calculate_potential_columns($container, $items[0]);
	}

	const first_top = $items[0].getBoundingClientRect().top;

	let items_per_row = 1;
	for (let i = 1; i < $items.length; i++) {
		const item_top = $items[i].getBoundingClientRect().top;
		if (Math.abs(item_top - first_top) > threshold) break;

		items_per_row++;
	}

	// If all items fit on one row, calculate how many could actually fit
	if (items_per_row === $items.length) {
		return calculate_potential_columns($container, $items[0]);
	}

	return items_per_row;
}


/**
 * Calculate how many columns could fit based on container and item widths
 * @param {HTMLElement} $container
 * @param {HTMLElement} $item - A sample item to measure
 * @return {int}
 */
function calculate_potential_columns($container, $item) {
	const styles = getComputedStyle($container);

	// Use getBoundingClientRect for fractional pixel precision
	const container_width = $container.getBoundingClientRect().width;
	const item_width = $item.getBoundingClientRect().width;
	const gap = parseFloat(styles.columnGap) || 0;

	if (item_width === 0) return 0;

	// Calculate how many items could fit: (container_width + gap) / (item_width + gap)
	// Add small epsilon (0.5px) to account for sub-pixel rounding before floor
	const potential = Math.floor((container_width + gap) / (item_width + gap) + 0.5);

	// Cap by the CSS --items-per-row property (the max from .:g-max-N class)
	const max_from_css = styles.getPropertyValue(items_per_row_prop);
	const max_items = max_from_css ? parseInt(max_from_css.trim()) : Infinity;

	return Math.min(potential, max_items);
}


/**
 * Get the natural --item-min-width value from CSS (without override)
 * @param {HTMLElement} $container
 * @return {number|null} Natural min width in pixels, or null if not set
 */
function get_natural_min_width($container) {
	// Get the natural --item-min-width (ignoring any inline style override)
	// Temporarily remove our override to read the base CSS value
	const had_override = $container.style.getPropertyValue(item_min_width_prop);
	$container.style.removeProperty(item_min_width_prop);

	// Force style recalculation by accessing offsetHeight
	void $container.offsetHeight;

	const natural_min_width_str = getComputedStyle($container).getPropertyValue(item_min_width_prop);
	const natural_min_width = parseFloat(natural_min_width_str);

	// Restore override if it existed
	if (had_override) {
		$container.style.setProperty(item_min_width_prop, had_override);
	}

	// Return null if no natural min-width is set in CSS
	if (!natural_min_width || isNaN(natural_min_width)) {
		return null;
	}

	return natural_min_width;
}


/**
 * Calculate theoretical columns without any auto-grid overrides
 * This tells us what the grid WOULD naturally do
 * @param {number} container_width - Container width in pixels
 * @param {number} gap - Column gap in pixels
 * @param {number} max_columns - Maximum columns from --items-per-row
 * @param {number} natural_min_width - Natural --item-min-width from CSS
 * @return {int}
 */
function calculate_theoretical_columns(container_width, gap, max_columns, natural_min_width) {
	// Calculate item max width based on max columns
	const item_max_width = (
		container_width - (gap * (max_columns - 1))
	) / max_columns;

	// Grid uses: minmax(max(min(viewport, natural_min_width), item_max_width), 1fr)
	// Effective min width is the larger of natural_min_width and item_max_width
	const effective_min_width = Math.max(natural_min_width, item_max_width);

	// Calculate how many columns fit with this effective min width.
	// A grid that fits exactly max_columns is a mathematically exact integer
	// here, but sub-pixel width rounding differs between rendering engines, so
	// it can measure as N.9999 in one browser and N.0 in another — Firefox then
	// floors to N-1 while Chrome/Safari get N, giving cards different internal
	// proportions. Add a sub-pixel tolerance before flooring so the count is
	// stable cross-browser (mirrors the epsilon in calculate_potential_columns).
	const subpixel_tolerance = 1;
	const theoretical = Math.floor(
		(container_width + gap + subpixel_tolerance) / (effective_min_width + gap)
	);

	return Math.min(theoretical, max_columns);
}


/**
 * Update a single container's data attribute
 * @param {HTMLElement} $container
 */
function update_container($container) {
	fastdom.measure(() => {
		// For auto grids, use theoretical columns; otherwise use actual
		let count, theoretical, should_override;
		let total_items = 0;
		let orphan_columns = [];
		let override_value = null;

		if (is_auto_grid($container)) {
			total_items = $container.children.length;
			const styles = getComputedStyle($container);
			const max_columns_str = styles.getPropertyValue(items_per_row_prop);
			const max_columns = parseInt(max_columns_str);

			// Abort if --items-per-row not set
			if (!max_columns || isNaN(max_columns)) {
				count = calculate_items_per_row($container);
				fastdom.mutate(() => {
					$container.setAttribute(row_items_attr, count);
				});
				return;
			}

			const gap = parseFloat(styles.columnGap) || 0;
			const container_width = $container.getBoundingClientRect().width;

			// Get the natural min-width from CSS
			const natural_min_width = get_natural_min_width($container);
			if (!natural_min_width) {
				count = calculate_items_per_row($container);
				fastdom.mutate(() => {
					$container.setAttribute(row_items_attr, count);
				});
				return;
			}

			// Calculate which columns to skip
			// Check for manual overrides first, fall back to automatic calculation
			orphan_columns = get_manual_skip_columns($container);
			if (orphan_columns === null) {
				orphan_columns = calculate_orphan_columns(total_items, max_columns);
			}

			// Calculate what the grid would naturally do (pass shared values)
			theoretical = calculate_theoretical_columns(container_width, gap, max_columns, natural_min_width);

			// Determine if we should override
			// Check if theoretical column count is in the skip list
			should_override = (
				orphan_columns.length > 0
				&& orphan_columns.includes(theoretical)
			);

			if (should_override) {
				// Calculate container width thresholds for this skip column
				const skip_column = theoretical;
				const skip_column_min_width = (skip_column * natural_min_width)
					+ ((skip_column - 1) * gap);
				const next_column_width = ((skip_column + 1) * natural_min_width)
					+ (skip_column * gap);

				// Only override when:
				// 1. Container is wide enough for skip column to occur (>= skip_column_min_width)
				// 2. Container is not yet wide enough for next column (< next_column_width + buffer)
				const wide_enough_for_skip = container_width >= skip_column_min_width;
				const not_yet_at_next = container_width < (next_column_width + 4);

				// Re-check: only override if we're in the valid range
				if (wide_enough_for_skip && not_yet_at_next) {
					// Calculate item width at the skip column
					const item_width_at_skip = (
						container_width - (gap * (skip_column - 1))
					) / skip_column;

					// Set min-width slightly above to force grid to use fewer columns
					// Using 2px buffer to account for sub-pixel rounding
					override_value = `${(item_width_at_skip + 2).toFixed(2)}px`;

					// Override will force one fewer column
					count = skip_column - 1;
				} else {
					// Outside valid range - use theoretical
					should_override = false;
					count = theoretical;
				}
			} else {
				// Theoretical doesn't match any skip columns - use it as-is
				count = theoretical;
			}
		} else {
			// Regular grid, just count actual items per row
			count = calculate_items_per_row($container);
		}

		fastdom.mutate(() => {
			$container.setAttribute(row_items_attr, count);

			// Set orphan prevention for auto grids
			if (is_auto_grid($container)) {
				$container.setAttribute(total_items_attr, total_items);

				if (orphan_columns.length > 0) {
					$container.setAttribute(skip_columns_attr, orphan_columns.join(','));
				} else {
					$container.removeAttribute(skip_columns_attr);
				}

				// Apply or remove override
				if (should_override && override_value) {
					$container.style.setProperty(item_min_width_prop, override_value);
				} else {
					$container.style.removeProperty(item_min_width_prop);
				}
			}
		});
	});
}


/**
 * Update all tracked containers
 */
function update_all() {
	if (!has_grid) return;

	$max_row_containers.forEach($container => {
		update_container($container);
	});
}


/**
 * Setup ResizeObserver for a container
 * @param {HTMLElement} $container
 */
function observe_container($container) {
	if (!resize_observer) {
		const handle_resize = util.throttle((entries) => {
			entries.forEach(entry => {
				update_container(entry.target);
			});
		}, 150);
		resize_observer = new ResizeObserver(handle_resize);
	}

	resize_observer.observe($container);
}


/**
 * Watch for dynamically added containers
 */
function observe_dynamic_content() {
	mutation_observer = new MutationObserver((mutations) => {
		mutations.forEach(mutation => {
			if (mutation.type !== 'childList') return;

			mutation.addedNodes.forEach($node => {
				if ($node.nodeType !== Node.ELEMENT_NODE) return;

				// Check if the added node itself is a grid container
				if ($node.matches && $node.matches(max_row_container_$)) {
					update_container($node);
					observe_container($node);
				}

				// Check for grid containers within the added node
				if ($node.querySelectorAll) {
					const $nested = $node.querySelectorAll(max_row_container_$);
					$nested.forEach($container => {
						update_container($container);
						observe_container($container);
					});
				}
			});
		});
	});

	mutation_observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
}


/**
 * Initialize all containers
 */
function init_containers() {
	has_grid = assign_elements();
	if (!has_grid) return;

	$max_row_containers.forEach($container => {
		update_container($container);
		observe_container($container);
	});

	observe_dynamic_content();

	// Style Studio (or any external tool) can dispatch this event after it
	// changes CSS vars that influence orphan calculations — the inline
	// --item-min-width override we cache otherwise masks the new natural value
	// until the next genuine size change.
	window.addEventListener('fritz:layout-recalc', () => {
		$max_row_containers.forEach($container => {
			$container.style.removeProperty(item_min_width_prop);
			update_container($container);
		});
	});
}


/**
 * Destroy
 */
function destroy() {
	if (resize_observer) {
		resize_observer.disconnect();
		resize_observer = null;
	}

	if (mutation_observer) {
		mutation_observer.disconnect();
		mutation_observer = null;
	}

	has_grid = false;
	$max_row_containers = null;
}
